/**
 * UserStatusPicker — Popover content for picking a workflow status.
 *
 * Shows system defaults (14 statuses) or custom statuses if provided.
 * Used by UserStatusDot and the multi-select "Set Status" action.
 * Follows §21 spec: colored squares + labels, "Clear status" at bottom.
 */

import { memo, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { bulkUpdateUserStatus } from "@/db/operations"
import {
  getSystemStatusItems,
  normalizeUserStatus,
  type CustomUserStatus,
} from "@/shared/constants/userStatus"
import CrossIcon from "@/shared/icons/totodoicons/stroke/cross.svg?react"
import {
  PickerItem,
  PickerHeader,
  PickerSeparator,
} from "@/shared/components/picker"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UserStatusPickerProps {
  /** The object ID(s) to update — single string or array for batch */
  taskId: string | string[]
  /** Currently selected userStatus value (raw from DB) */
  currentValue?: string | number | null
  /** Custom statuses from the container (project/list), if any */
  customStatuses?: CustomUserStatus[]
  /** Called after a status is selected */
  onClose: () => void
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const UserStatusPicker = memo(function UserStatusPicker({
  taskId,
  currentValue,
  customStatuses,
  onClose,
}: UserStatusPickerProps) {
  const { t } = useTranslation()
  const normalizedCurrent = normalizeUserStatus(currentValue)

  // Resolve which items to show
  const items = customStatuses?.length
    ? customStatuses
        .filter((s) => s.isActive)
        .map((s) => ({
          id: s.id,
          label: s.label,
          color: s.color,
        }))
    : getSystemStatusItems().map((s) => ({
        id: s.id,
        label: s.label,
        color: s.color,
      }))

  const handleSelect = useCallback(
    async (statusId: string) => {
      const ids = Array.isArray(taskId) ? taskId : [taskId]
      await bulkUpdateUserStatus(ids, statusId)
      onClose()
    },
    [taskId, onClose],
  )

  const handleClear = useCallback(async () => {
    const ids = Array.isArray(taskId) ? taskId : [taskId]
    await bulkUpdateUserStatus(ids, undefined)
    onClose()
  }, [taskId, onClose])

  return (
    // `max-h-[inherit]` only resolves to a real bound when the immediate
    // parent sets `max-height` as a literal value (e.g. DropdownMenuSubContent,
    // PopoverContent) — kept for those callers. `min-h-0 flex-1` additionally
    // makes this fill and shrink correctly when the parent is a plain flex
    // column with no literal max-height of its own (e.g. ChecklistStatusMenu's
    // scroll region). The two strategies don't conflict: whichever the parent
    // actually provides wins.
    <div className="flex min-h-0 max-h-[inherit] flex-1 flex-col gap-0.5 overflow-hidden">
      <PickerHeader>{t("Status")}</PickerHeader>

      {/* Status list — scrollable when taller than viewport */}
      <div className="min-h-0 overflow-y-auto">
        {items.map((item) => (
          <PickerItem
            key={item.id}
            selected={normalizedCurrent === item.id}
            icon={
              <span
                className="size-4 shrink-0 rounded"
                style={{ backgroundColor: item.color }}
              />
            }
            onClick={() => handleSelect(item.id)}
          >
            {item.label}
          </PickerItem>
        ))}
      </div>

      {/* Divider + Clear */}
      {normalizedCurrent && (
        <div className="shrink-0">
          <PickerSeparator />
          <PickerItem
            variant="destructive"
            icon={<CrossIcon className="size-4" />}
            onClick={handleClear}
            checkmark={false}
          >
            {t("Clear status")}
          </PickerItem>
        </div>
      )}
    </div>
  )
})
